/**
 * AddressesTab — "العناوين المحفوظة"
 *
 * Shows all saved delivery addresses, lets the user add / edit / delete
 * each one, mark one as default, and (if a Google Maps key is present at
 * runtime) pin the exact location on a map. Without a map key we still
 * accept lat/lng typed manually.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Loader2, Plus, Trash2, Edit2, Star, X, Phone, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import AddressMapPicker from './AddressMapPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMIRATES = ['دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'رأس الخيمة', 'الفجيرة', 'أم القيوين'];

const EMPTY = {
  label: '',
  full_name: '',
  phone: '',
  emirate: 'دبي',
  area: '',
  address: '',
  notes: '',
  lat: null,
  lng: null,
  is_default: false,
};

export default function AddressesTab({ isAr, isDarkMode }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | { ...address }
  const [saving, setSaving] = useState(false);

  const token = () => localStorage.getItem('token');
  const authCfg = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/customer/addresses`, authCfg());
      setList(res.data?.addresses || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!editing.label.trim() || !editing.full_name.trim() || !editing.phone.trim() || !editing.area.trim() || !editing.address.trim()) {
      toast.error(isAr ? 'يرجى إكمال كل الحقول' : 'Please complete all fields');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (editing.id) {
        await axios.put(`${BACKEND_URL}/api/customer/addresses/${editing.id}`, payload, authCfg());
      } else {
        await axios.post(`${BACKEND_URL}/api/customer/addresses`, payload, authCfg());
      }
      toast.success(isAr ? 'تم الحفظ ✓' : 'Saved ✓');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر الحفظ' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isAr ? 'حذف هذا العنوان؟' : 'Delete this address?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/customer/addresses/${id}`, authCfg());
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      setList(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر الحذف' : 'Failed'));
    }
  };

  const setDefault = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/customer/addresses/${id}/default`, {}, authCfg());
      toast.success(isAr ? 'تم تعيينه كافتراضي' : 'Set as default');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر التحديث' : 'Failed'));
    }
  };

  const cardBase = isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light';
  const titleText = isDarkMode ? 'text-white' : 'text-[#0f172a]';
  const subText = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-gray-200';

  if (loading) {
    return (
      <div className={`rounded-2xl border p-10 flex items-center justify-center ${cardBase}`}>
        <Loader2 className="h-6 w-6 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="addresses-tab">
      <div className={`rounded-2xl border p-6 ${cardBase}`}>
        <div className="flex items-center justify-between mb-5">
          <h2 className={`text-lg font-bold flex items-center gap-2 ${titleText}`}>
            <MapPin className="h-5 w-5 text-[#1E9BD9]" />
            {isAr ? 'العناوين المحفوظة' : 'Saved addresses'}
          </h2>
          <Button
            size="sm"
            onClick={() => setEditing({ ...EMPTY })}
            data-testid="add-address-btn"
            className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white"
          >
            <Plus className="h-4 w-4 me-1" />
            {isAr ? 'عنوان جديد' : 'New address'}
          </Button>
        </div>

        {list.length === 0 ? (
          <div className={`text-center py-10 rounded-xl border-dashed border ${isDarkMode ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-500'}`}>
            {isAr ? 'لا توجد عناوين بعد — أضف عنوانك الأول.' : 'No addresses yet — add your first one.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {list.map(a => (
              <div
                key={a.id}
                data-testid={`address-card-${a.id}`}
                className={`relative rounded-2xl border p-5 transition-all ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 hover:border-[#1E9BD9]/30' : 'bg-gray-50 border-gray-200 hover:border-[#1E9BD9]/40'} ${a.is_default ? 'ring-2 ring-[#1E9BD9]/40' : ''}`}
              >
                {a.is_default && (
                  <span className="absolute top-3 end-3 inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-[#1E9BD9]/15 text-[#1E9BD9]">
                    <Star className="h-3 w-3" /> {isAr ? 'افتراضي' : 'Default'}
                  </span>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <Home className="h-4 w-4 text-[#1E9BD9]" />
                  <span className={`font-bold ${titleText}`}>{a.label}</span>
                </div>
                <div className={`text-sm ${subText}`}>{a.full_name}</div>
                <div className={`text-sm flex items-center gap-1 ${subText}`}>
                  <Phone className="h-3.5 w-3.5" /> {a.phone}
                </div>
                <div className={`text-sm mt-2 ${titleText}`}>{a.address}</div>
                <div className={`text-xs ${subText}`}>{a.area}، {a.emirate}</div>
                {a.lat != null && a.lng != null && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${a.lat},${a.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#1E9BD9] hover:underline mt-2"
                  >
                    <MapPin className="h-3.5 w-3.5" /> {isAr ? 'عرض على الخريطة' : 'View on map'}
                  </a>
                )}

                <div className={`flex items-center gap-2 mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  {!a.is_default && (
                    <button
                      type="button"
                      onClick={() => setDefault(a.id)}
                      data-testid={`set-default-${a.id}`}
                      className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#1E9BD9]/10 text-[#1E9BD9] hover:bg-[#1E9BD9]/20 transition-colors"
                    >
                      {isAr ? 'تعيين افتراضي' : 'Make default'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditing(a)}
                    data-testid={`edit-address-${a.id}`}
                    className={`ms-auto p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                    aria-label={isAr ? 'تعديل' : 'Edit'}
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(a.id)}
                    data-testid={`remove-address-${a.id}`}
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-500/10"
                    aria-label={isAr ? 'حذف' : 'Delete'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !saving && setEditing(null)}>
          <div
            className={`w-full max-w-xl rounded-2xl border max-h-[90vh] overflow-y-auto ${cardBase}`}
            onClick={e => e.stopPropagation()}
            data-testid="address-edit-modal"
          >
            <div className={`flex items-center justify-between p-5 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
              <h3 className={`text-lg font-bold ${titleText}`}>
                {editing.id ? (isAr ? 'تعديل العنوان' : 'Edit address') : (isAr ? 'إضافة عنوان جديد' : 'Add new address')}
              </h3>
              <button type="button" onClick={() => !saving && setEditing(null)} className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>{isAr ? 'التسمية' : 'Label'}</Label>
                <Input value={editing.label} onChange={e => setEditing({ ...editing, label: e.target.value })} placeholder={isAr ? 'المنزل / العمل' : 'Home / Work'} className={`mt-2 ${inputCls}`} data-testid="addr-label-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''}>{isAr ? 'الاسم الكامل' : 'Full name'}</Label>
                  <Input value={editing.full_name} onChange={e => setEditing({ ...editing, full_name: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="addr-fullname-input" />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''}>{isAr ? 'الهاتف' : 'Phone'}</Label>
                  <Input value={editing.phone} onChange={e => setEditing({ ...editing, phone: e.target.value })} placeholder="+971 50 123 4567" className={`mt-2 ${inputCls}`} data-testid="addr-phone-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''}>{isAr ? 'الإمارة' : 'Emirate'}</Label>
                  <select
                    value={editing.emirate}
                    onChange={e => setEditing({ ...editing, emirate: e.target.value })}
                    className={`w-full mt-2 h-10 px-3 rounded-md border text-sm ${inputCls}`}
                    data-testid="addr-emirate-select"
                  >
                    {EMIRATES.map(em => <option key={em} value={em}>{em}</option>)}
                  </select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''}>{isAr ? 'المنطقة' : 'Area'}</Label>
                  <Input value={editing.area} onChange={e => setEditing({ ...editing, area: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="addr-area-input" />
                </div>
              </div>
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>{isAr ? 'العنوان التفصيلي' : 'Street address'}</Label>
                <Textarea value={editing.address} onChange={e => setEditing({ ...editing, address: e.target.value })} rows={2} className={`mt-2 ${inputCls}`} data-testid="addr-address-input" />
              </div>

              <AddressMapPicker
                isAr={isAr}
                isDarkMode={isDarkMode}
                lat={editing.lat}
                lng={editing.lng}
                onChange={({ lat, lng }) => setEditing({ ...editing, lat, lng })}
              />

              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="addr-default-toggle">
                <input
                  type="checkbox"
                  checked={!!editing.is_default}
                  onChange={e => setEditing({ ...editing, is_default: e.target.checked })}
                  className="h-4 w-4 accent-[#1E9BD9]"
                />
                <span className={`text-sm ${titleText}`}>{isAr ? 'حفظ كعنوان افتراضي' : 'Save as default address'}</span>
              </label>
            </div>

            <div className={`flex items-center justify-between p-5 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
              <Button variant="ghost" onClick={() => !saving && setEditing(null)} data-testid="addr-cancel-btn">
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={save} disabled={saving} data-testid="addr-save-btn" className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white px-6">
                {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                {isAr ? 'حفظ' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
